import React, { useEffect, useRef, useState } from 'react'

import { AdjacentInputWrapper, InputWrapper, Label } from './Form/css'
import { Flex } from './Layout'
import { timeStringFromMS } from '@sportninja/common/utils/utils'
import {
  clockFromMinutesAndSeconds,
  readClockMinutesAndSeconds,
} from '@sportninja/common/features/game/scoring/utils/periodClock'

/** The most minutes a long clock takes: the API reads three digits of them. */
const LONG_CLOCK_MAX_MINUTES = 999

export const periodClockValidator = (values) => {
  let message = false

  if (values.duration === 0) {
    message = 'Please enter a period length greater than 0'
  }
  return message && { duration: message }
}

const parseClockTime = (clockTime) => {
  if (!clockTime || clockTime.length === 0) {
    return ['', '']
  }

  const bits = clockTime.split(':')
  if (bits.length === 2) {
    return [parseInt(bits[0], 10), parseInt(bits[1], 10)]
  }

  if (bits.length === 3) {
    return [parseInt(bits[1], 10), parseInt(bits[2], 10)]
  }
}

const padNum = (num) => {
  const str = `${num}`
  if (str.length < 2) {
    return `0${str}`
  } else {
    return str
  }
}

const getClockTime = ({ min, sec }) => {
  return `00:${padNum(min)}:${padNum(sec)}`
}

// A long clock can run past the hour, so its hours are shown in the minutes:
// `01:05:00` is 65:00.
const parseLongClockTime = (clockTime) => {
  const clock = readClockMinutesAndSeconds(clockTime)
  return clock ? [clock.minutes, clock.seconds] : ['', '']
}

// The clock to store, or `undefined` while the boxes are not a clock: the
// form's required check then stops the submit, rather than a clock being sent
// that is not one.
const getLongClockTime = ({ min, sec }) => {
  return clockFromMinutesAndSeconds(min, sec) ?? undefined
}

const parseTime = (defaultValue, isDuration, longClock) => {
  if (isDuration) {
    return timeStringFromMS(defaultValue, true)
  }
  return longClock
    ? parseLongClockTime(defaultValue)
    : parseClockTime(defaultValue)
}

const PeriodClockTime = ({
  defaultValue,
  disabled,
  hasError,
  isDuration,
  label,
  longClock = false,
  name,
  noFlex,
  onChange,
  required,
}) => {
  const [min, sec] = parseTime(defaultValue, isDuration, longClock)
  const [time, setTime] = useState({ min, sec })
  // The boxes as last set, by a change or by a new default, ahead of the
  // render that shows them: a change builds on them, as an update function
  // would, without running inside one.
  const latestTime = useRef(time)

  useEffect(() => {
    const [min, sec] = parseTime(defaultValue, isDuration, longClock)
    latestTime.current = { min, sec }
    setTime({ min, sec })
  }, [defaultValue, isDuration, longClock])

  // The form hears of a change here, in the event, and never from inside a
  // state update: React can apply an update more than once, and each time the
  // form's own updates would start another render, without end.
  const handleChange = (key, e) => {
    const newState = { ...latestTime.current, [key]: e.target.value }
    latestTime.current = newState
    setTime(newState)

    // Convert to milliseconds
    const value = isDuration
      ? (parseInt(newState.min, 10) * 60 + parseInt(newState.sec, 10)) * 1000
      : longClock
      ? getLongClockTime(newState)
      : getClockTime(newState)

    onChange({ target: { name, value } })
  }

  return (
    <InputWrapper disabled={disabled} noFlex={noFlex}>
      <Label disabled={disabled}>
        {label}
        {required ? ' *' : ''}
      </Label>
      <Flex>
        <AdjacentInputWrapper disabled={disabled}>
          <input
            disabled={disabled}
            value={time.min}
            onChange={handleChange.bind(this, 'min')}
            className={`left ${hasError ? 'error' : ''}`}
            type='number'
            min={0}
            max={longClock ? LONG_CLOCK_MAX_MINUTES : 60}
            step={1}
            required
          />
          <span>{isDuration ? 'min' : ':'}</span>
        </AdjacentInputWrapper>
        <AdjacentInputWrapper disabled={disabled}>
          <input
            disabled={disabled}
            value={time.sec}
            onChange={handleChange.bind(this, 'sec')}
            className={`right ${hasError ? 'error' : ''}`}
            type='number'
            min={0}
            max={59}
            step={1}
            required
          />
          {isDuration && <span>sec</span>}
        </AdjacentInputWrapper>
      </Flex>
    </InputWrapper>
  )
}

export default PeriodClockTime
